import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useData } from '../contexts/DataContext';
import { useRoleCheck } from '../components/RoleGuard';
import { Cliente, PiattaformaSocial } from '../types';
import { DelightfulEmptyState } from '../components/DelightfulInteractions';
import { QuickSuccessToast } from '../components/SuccessStates';
import Button from '../components/ui/Button';
import ReportGeneratorModal from '../components/ReportGeneratorModal';
import ReportStorico from '../components/ReportStorico';
import ClientIntelSources from '../components/ClientIntelSources';
import {
  Plus,
  AlertTriangle,
  Building2,
  Trash2,
  Users,
  Archive,
  RefreshCw,
  ArrowLeft,
  FolderOpen,
  ExternalLink,
  FileText,
  Radar
} from 'lucide-react';
import { usePageState } from '../hooks/usePageState';
import { giorniUscita, anteprimaPiano } from '../utils/pianoUscite';
import { dividiAccount } from '../utils/accountDestinazione';

// Stile piatto della scheda cliente (stesso impianto della pagina Analisi):
// etichette piccole in maiuscolo, campi con un filetto e raggio 6, niente
// riquadri. Solo classi: stato e handler del form non cambiano.
const CAMPO = 'w-full px-3 py-2 rounded-md border border-slate-200 text-slate-900 bg-white focus:border-slate-400 focus:ring-0 focus:outline-none transition-colors disabled:bg-transparent disabled:border-transparent disabled:px-0 disabled:text-slate-900 text-base sm:text-sm';
const ETICHETTA = 'block mb-1.5 text-[10.5px] font-semibold uppercase tracking-[1.1px] text-slate-500';
const CHIP = (attivo: boolean) =>
  `px-3 py-1.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium transition-colors min-h-[36px] sm:min-h-0 border ${
    attivo
      ? 'bg-[#13161C] border-[#13161C] text-[#ffffff]'
      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
  } disabled:cursor-not-allowed`;
// Sezione del form: filetto sopra e titolo piccolo
const SEZIONE = 'pt-5 border-t border-slate-200/70';
const TITOLO_SEZIONE = 'mb-3 text-sm font-semibold text-slate-900';

// Per ogni social: nome della pagina/account + password. Le chiavi puntano ai
// campi di formData, i blocchi compaiono solo per le piattaforme selezionate.
const CAMPI_SOCIAL = [
  {
    piattaforma: PiattaformaSocial.INSTAGRAM,
    campoNome: 'nomeInstagram',
    campoPassword: 'passwordInstagram',
    etichettaNome: 'Nome pagina Instagram',
    placeholderNome: 'es. bymarke.it',
    notaNome: 'Handle senza @ — usato anche per generare i report social'
  },
  {
    piattaforma: PiattaformaSocial.TIKTOK,
    campoNome: 'nomeTikTok',
    campoPassword: 'passwordTikTok',
    etichettaNome: 'Nome pagina TikTok',
    placeholderNome: 'es. bymarke.it',
    notaNome: ''
  },
  {
    piattaforma: PiattaformaSocial.YOUTUBE,
    campoNome: 'nomeYouTube',
    campoPassword: 'passwordYouTube',
    etichettaNome: 'Nome canale YouTube',
    placeholderNome: 'es. ByMarke',
    notaNome: ''
  },
  {
    piattaforma: PiattaformaSocial.FACEBOOK,
    campoNome: 'nomeFacebook',
    campoPassword: 'passwordFacebook',
    etichettaNome: 'Nome pagina Facebook',
    placeholderNome: 'es. ByMarke',
    notaNome: ''
  },
  {
    piattaforma: PiattaformaSocial.LINKEDIN,
    campoNome: 'nomeLinkedIn',
    campoPassword: 'passwordLinkedIn',
    etichettaNome: 'Nome pagina LinkedIn',
    placeholderNome: 'es. ByMarke Srl',
    notaNome: ''
  }
] as const;

const Clienti: React.FC = () => {
  const { state, addCliente, updateCliente, deleteCliente, archiveCliente, loadClienti } = useData();
  // Chi può scrivere sui clienti: l'amministratore e il publisher (è lui a
  // pubblicare, quindi gli servono nome pagina, orari e credenziali aggiornati).
  // Archiviare ed eliminare restano dell'amministratore: il backend rifiuta
  // comunque le richieste degli altri ruoli.
  const { isAdmin, isPublisher } = useRoleCheck();
  const puoModificare = isAdmin() || isPublisher();
  const puoArchiviareEliminare = isAdmin();
  // Scheda cliente = pagina interna stile Copywriter: click sulla card apre la
  // scheda del cliente con i tab (Info / Report Social). isNuovo = form di
  // creazione (senza tab).
  // Scheda aperta, tab e ricerca stanno nell'URL: tornando indietro con la
  // freccia o ricaricando si ritrova la stessa schermata.
  const [pageState, setPageState] = usePageState(
    { client: '', tab: 'active', scheda: 'info', q: '' },
    { push: ['client', 'tab', 'scheda'] }
  );
  const selectedCliente = useMemo(
    () => (pageState.client ? state.clienti.find(c => c.id === pageState.client) ?? null : null),
    [pageState.client, state.clienti]
  );
  const [isNuovo, setIsNuovo] = useState(false);
  const schedaTab = pageState.scheda as 'info' | 'report' | 'intel';
  const setSchedaTab = (tab: 'info' | 'report' | 'intel') => setPageState({ scheda: tab });
  // Incrementato a ogni report generato → ricarica la sezione "Report passati"
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  // True se il cliente selezionato NON ha il collegamento IG diretto nel
  // Business Manager → alert rosso nel tab Report Social (la generazione fallirebbe)
  const [igNonCollegato, setIgNonCollegato] = useState(false);

  useEffect(() => {
    if (schedaTab !== 'report' || !selectedCliente) { setIgNonCollegato(false); return; }
    let attivo = true;
    fetch(`/api/reports/status/${selectedCliente.id}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('bymarke_token')}` },
    })
      .then((r) => r.json())
      .then((j) => { if (attivo && j?.success) setIgNonCollegato(!j.igConnected); })
      .catch(() => { /* in caso di errore non mostriamo l'alert */ });
    return () => { attivo = false; };
  }, [schedaTab, selectedCliente]);
  // Il form della scheda è editabile per amministratori e publisher; per gli
  // altri ruoli resta consultabile in sola lettura
  const isViewMode = !puoModificare;
  const activeTab = pageState.tab as 'active' | 'archived';
  const setActiveTab = (tab: 'active' | 'archived') => setPageState({ tab });

  // Success toast states
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Stati per filtri
  const searchTerm = pageState.q;
  const setSearchTerm = (value: string) => setPageState({ q: value });
  const [filteredClienti, setFilteredClienti] = useState<Cliente[]>(state.clienti);

  const [formData, setFormData] = useState({
    nomeAzienda: '',
    referenteRiprese: '',
    telefono: '',
    email: '',
    reelMensili: 4,
    testiDalCliente: false,
    postGrafici: false,
    // Nel form: postGraficiSettimana = solo i post grafici, caroselliFotoSettimana = i caroselli foto.
    // Al salvataggio postGraficiSettimana diventa il totale (vedi types: Cliente.caroselliFotoSettimana).
    postGraficiSettimana: 1,
    caroselliFotoSettimana: 0,
    accountPubblicazione: '',
    orarioPubblicazione: '21:00',
    giorniPreferitiRiprese: [] as string[],
    piattaforme: [] as PiattaformaSocial[],
    nomeInstagram: '',
    passwordInstagram: '',
    nomeTikTok: '',
    passwordTikTok: '',
    nomeYouTube: '',
    passwordYouTube: '',
    nomeFacebook: '',
    passwordFacebook: '',
    nomeLinkedIn: '',
    passwordLinkedIn: '',
    linkGoogleDrive: '',
    linkWebAppLead: '',
    logoUrl: ''
  });

  const giorniSettimana = [
    'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'
  ];

  // I dati vengono caricati automaticamente dal DataProvider

  // Effetto per aggiornare la lista filtrata
  useEffect(() => {
    const filtered = state.clienti.filter(cliente => {
      // Filter by status
      if (activeTab === 'active' && cliente.status === 'archived') return false;
      if (activeTab === 'archived' && cliente.status !== 'archived') return false;

      // Filter by search term
      if (searchTerm && !cliente.nomeAzienda.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      return true;
    });

    filtered.sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }));

    setFilteredClienti(filtered);
  }, [state.clienti, searchTerm, activeTab]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validazione base
    if (!formData.nomeAzienda.trim()) {
      alert('Il nome dell\'azienda è obbligatorio');
      return;
    }

    if (!formData.telefono.trim()) {
      alert('Il numero di telefono è obbligatorio');
      return;
    }

    if (formData.reelMensili <= 0) {
      alert('Il numero di reel mensili deve essere maggiore di 0');
      return;
    }

    if (formData.piattaforme.length === 0) {
      alert('Seleziona almeno una piattaforma social');
      return;
    }

    const postTotali = formData.postGraficiSettimana + formData.caroselliFotoSettimana;
    if (formData.postGrafici && (postTotali < 1 || postTotali > 7)) {
      alert('Indica quanti post grafici e quanti caroselli foto vanno pubblicati ogni settimana (insieme da 1 a 7)');
      return;
    }

    try {
      const clienteData = {
        nomeAzienda: formData.nomeAzienda.trim(),
        referenteRiprese: formData.referenteRiprese.trim() || undefined,
        telefono: formData.telefono.trim(),
        email: formData.email.trim() || undefined,
        reelMensili: formData.reelMensili,
        testiDalCliente: formData.testiDalCliente,
        postGrafici: formData.postGrafici,
        postGraficiSettimana: formData.postGrafici ? postTotali : 0,
        caroselliFotoSettimana: formData.postGrafici ? formData.caroselliFotoSettimana : 0,
        accountPubblicazione: dividiAccount(formData.accountPubblicazione),
        orarioPubblicazione: formData.orarioPubblicazione,
        giorniPreferitiRiprese: [...formData.giorniPreferitiRiprese],
        formatReel: [],
        piattaforme: [...formData.piattaforme],
        nomeInstagram: formData.nomeInstagram.trim(),
        passwordInstagram: formData.passwordInstagram.trim(),
        nomeTikTok: formData.nomeTikTok.trim(),
        passwordTikTok: formData.passwordTikTok.trim(),
        nomeYouTube: formData.nomeYouTube.trim(),
        passwordYouTube: formData.passwordYouTube.trim(),
        nomeFacebook: formData.nomeFacebook.trim(),
        passwordFacebook: formData.passwordFacebook.trim(),
        nomeLinkedIn: formData.nomeLinkedIn.trim(),
        passwordLinkedIn: formData.passwordLinkedIn.trim(),
        linkGoogleDrive: formData.linkGoogleDrive.trim(),
        linkWebAppLead: formData.linkWebAppLead.trim(),
        logoUrl: formData.logoUrl.trim() || undefined,
        status: 'active' as const
      };

      if (selectedCliente) {
        // Modifica cliente esistente
        updateCliente(selectedCliente.id, clienteData);
        setSuccessMessage(`${clienteData.nomeAzienda} aggiornato con successo!`);
      } else {
        // Nuovo cliente
        addCliente(clienteData);
        setSuccessMessage(`${clienteData.nomeAzienda} aggiunto al team ByMarke!`);
      }

      resetForm();
      chiudiScheda();
      setShowSuccessToast(true);
    } catch (error) {
      console.error('Errore nel salvare il cliente:', error);
      alert('Errore nel salvare il cliente. Riprova.');
    }
  };

  const resetForm = () => {
    setFormData({
      nomeAzienda: '',
      referenteRiprese: '',
      telefono: '',
      email: '',
      reelMensili: 4,
      testiDalCliente: false,
      postGrafici: false,
      postGraficiSettimana: 1,
      caroselliFotoSettimana: 0,
      accountPubblicazione: '',
      orarioPubblicazione: '21:00',
      giorniPreferitiRiprese: [],
      piattaforme: [],
      nomeInstagram: '',
      passwordInstagram: '',
      nomeTikTok: '',
      passwordTikTok: '',
      nomeYouTube: '',
      passwordYouTube: '',
      nomeFacebook: '',
      passwordFacebook: '',
      nomeLinkedIn: '',
      passwordLinkedIn: '',
      linkGoogleDrive: '',
      linkWebAppLead: '',
      logoUrl: ''
    });
  };

  // Campi del form ricavati dalla scheda cliente
  const formFromCliente = (cliente: Cliente) => ({
      nomeAzienda: cliente.nomeAzienda,
      referenteRiprese: cliente.referenteRiprese || '',
      telefono: cliente.telefono,
      email: cliente.email || '',
      reelMensili: cliente.reelMensili || 1,
      testiDalCliente: !!cliente.testiDalCliente,
      postGrafici: !!cliente.postGrafici,
      // Il totale salvato meno i caroselli foto = i post grafici
      postGraficiSettimana: Math.max(0, (cliente.postGraficiSettimana || 1) - (cliente.caroselliFotoSettimana || 0)),
      caroselliFotoSettimana: cliente.caroselliFotoSettimana || 0,
      accountPubblicazione: (cliente.accountPubblicazione || []).join(', '),
      orarioPubblicazione: cliente.orarioPubblicazione || '21:00',
      giorniPreferitiRiprese: cliente.giorniPreferitiRiprese,
      piattaforme: cliente.piattaforme,
      nomeInstagram: cliente.nomeInstagram || '',
      passwordInstagram: cliente.passwordInstagram || '',
      nomeTikTok: cliente.nomeTikTok || '',
      passwordTikTok: cliente.passwordTikTok || '',
      nomeYouTube: cliente.nomeYouTube || '',
      passwordYouTube: cliente.passwordYouTube || '',
      nomeFacebook: cliente.nomeFacebook || '',
      passwordFacebook: cliente.passwordFacebook || '',
      nomeLinkedIn: cliente.nomeLinkedIn || '',
      passwordLinkedIn: cliente.passwordLinkedIn || '',
      linkGoogleDrive: cliente.linkGoogleDrive || '',
      linkWebAppLead: cliente.linkWebAppLead || '',
      logoUrl: cliente.logoUrl || ''
  });

  // Precompila il form quando la scheda si apre. Vive in un effetto (e non nel
  // click) perché al refresh il cliente arriva dall'URL e i dati sono
  // disponibili solo dopo il caricamento.
  const formPopolatoPerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedCliente) {
      formPopolatoPerRef.current = null;
      return;
    }
    if (formPopolatoPerRef.current === selectedCliente.id) return;
    formPopolatoPerRef.current = selectedCliente.id;
    setFormData(formFromCliente(selectedCliente));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCliente]);

  // Apre la scheda interna del cliente (tab Info)
  const apriCliente = (cliente: Cliente) => {
    setIsNuovo(false);
    setPageState({ client: cliente.id, scheda: 'info' });
  };

  // Torna alla lista clienti
  const chiudiScheda = () => {
    setIsNuovo(false);
    setPageState({ client: '', scheda: 'info' });
  };

  const handleDelete = (clienteId: string) => {
    console.log('handleDelete chiamata per cliente ID:', clienteId);
    const cliente = state.clienti.find(c => c.id === clienteId);
    console.log('Cliente trovato:', cliente ? `${cliente.nomeAzienda} (${cliente.status})` : 'Non trovato');

    const confirmMessage = cliente?.status === 'archived'
      ? 'Sei sicuro di voler eliminare definitivamente questo cliente archiviato? Questa azione non può essere annullata.'
      : 'Sei sicuro di voler eliminare questo cliente?';

    console.log('Mostrando conferma:', confirmMessage);

    if (window.confirm(confirmMessage)) {
      console.log('Utente ha confermato, chiamando deleteCliente...');
      deleteCliente(clienteId);
      chiudiScheda();
    } else {
      console.log('Utente ha annullato l\'eliminazione');
    }
  };

  const handleArchive = (clienteId: string) => {
    if (window.confirm('Sei sicuro di voler archiviare questo cliente? Non collaborerai più con loro.')) {
      archiveCliente(clienteId);
      chiudiScheda();
    }
  };

  const handleUnarchive = (clienteId: string) => {
    if (window.confirm('Vuoi riattivare questo cliente?')) {
      updateCliente(clienteId, { status: 'active' });
      chiudiScheda();
    }
  };

  const toggleGiorno = (giorno: string) => {
    if (isViewMode) return;

    setFormData(prev => ({
      ...prev,
      giorniPreferitiRiprese: prev.giorniPreferitiRiprese.includes(giorno)
        ? prev.giorniPreferitiRiprese.filter(g => g !== giorno)
        : [...prev.giorniPreferitiRiprese, giorno]
    }));
  };

  const togglePiattaforma = (piattaforma: PiattaformaSocial) => {
    if (isViewMode) return;

    setFormData(prev => ({
      ...prev,
      piattaforme: prev.piattaforme.includes(piattaforma)
        ? prev.piattaforme.filter(p => p !== piattaforma)
        : [...prev.piattaforme, piattaforma]
    }));
  };


  return (
    <div className="p-4 sm:p-6 overflow-x-hidden max-w-full">
      {/* ===== VISTA LISTA — nascosta quando è aperta la scheda cliente ===== */}
      {!selectedCliente && !isNuovo && (
      <>
      {/* Header con titolo minimalista */}
      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
          Clienti
        </h1>
      </div>

      {/* Toolbar: Nuovo Cliente + Tabs + Ricerca su una riga */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4 sm:mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Add Button */}
          {puoModificare && (
            <Button
              onClick={() => {
                resetForm();
                setIsNuovo(true);
              }}
              variant="primary"
              icon={Plus}
              size="sm"
            >
              Nuovo
            </Button>
          )}

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-slate-100 dark:bg-gray-700 rounded-lg">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === 'active'
                  ? 'bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              Attivi
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === 'archived'
                  ? 'bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              Archiviati
            </button>
          </div>
        </div>

        {/* Ricerca */}
        <div className="flex-1 sm:max-w-xs">
          <input
            type="text"
            placeholder="Cerca cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-slate-100"
          />
        </div>
      </div>

      {/* Client List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 min-w-0">
        {filteredClienti.length === 0 ? (
          <div className="col-span-full">
            <DelightfulEmptyState
              icon={<Building2 className="w-12 h-12" />}
              title={activeTab === 'active' ? 'Nessun cliente attivo' : 'Nessun cliente archiviato'}
              description={
                activeTab === 'active'
                  ? 'Inizia la tua avventura ByMarke aggiungendo il primo cliente!'
                  : 'I clienti archiviati appariranno qui. Per ora tutto pulito!'
              }
              actionText={activeTab === 'active' ? 'Aggiungi il primo cliente' : undefined}
              onAction={activeTab === 'active' ? () => {
                resetForm();
                setIsNuovo(true);
              } : undefined}
            />
          </div>
        ) : (
          filteredClienti.map((cliente) => (
            <div
              key={cliente.id}
              onClick={() => apriCliente(cliente)}
              className="p-3 sm:p-4 rounded-xl border bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              {/* Header con logo e nome */}
              <div className="flex items-center gap-3 mb-3">
                {/* Logo cliente */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-slate-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
                  {cliente.logoUrl ? (
                    <img
                      src={cliente.logoUrl}
                      alt={cliente.nomeAzienda}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm font-medium">
                      {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {cliente.nomeAzienda}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {cliente.referenteRiprese || cliente.telefono}
                  </p>
                </div>
              </div>

              {/* Info */}
              <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-3">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{cliente.reelMensili || 0}</span> Reels/mese
                {cliente.postGrafici && (cliente.postGraficiSettimana || 0) > 0 && (() => {
                  const foto = Math.min(cliente.caroselliFotoSettimana || 0, cliente.postGraficiSettimana || 0);
                  const grafici = (cliente.postGraficiSettimana || 0) - foto;
                  return (
                  <>
                    {grafici > 0 && (<>
                      <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100">{grafici}</span> post grafici/sett.
                    </>)}
                    {foto > 0 && (<>
                      <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100">{foto}</span> caroselli foto/sett.
                    </>)}
                  </>
                  );
                })()}
                <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                pubbl. <span className="font-semibold text-slate-900 dark:text-slate-100">{cliente.orarioPubblicazione || '21:00'}</span>
              </div>

              {/* Piattaforme */}
              <div className="flex flex-wrap gap-1 mb-3">
                {cliente.piattaforme.map((platform) => (
                  <span
                    key={platform}
                    className="px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300"
                  >
                    {platform}
                  </span>
                ))}
              </div>

              {/* Hint: la card si apre come pagina interna del cliente */}
              <div className="flex items-center justify-end pt-2 border-t border-slate-100 dark:border-gray-700 text-xs font-medium text-slate-400 dark:text-slate-500">
                Apri scheda →
              </div>
            </div>
          ))
        )}
      </div>

      </>
      )}

      {/* Success Toast */}
      <QuickSuccessToast
        show={showSuccessToast}
        message={successMessage}
        icon={<Users className="w-5 h-5" />}
        onClose={() => setShowSuccessToast(false)}
      />

      {/* ===== SCHEDA CLIENTE — pagina interna con tab (stile Copywriter) ===== */}
      {(selectedCliente || isNuovo) && (
        <div>
          {/* Header: back + nome cliente + azioni */}
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                onClick={chiudiScheda}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                title="Torna ai clienti"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ letterSpacing: '-0.02em' }}>
                  {isNuovo ? 'Nuovo Cliente' : selectedCliente?.nomeAzienda}
                </h1>
                {!isNuovo && (
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Scheda cliente</p>
                )}
              </div>
            </div>
            {!isNuovo && selectedCliente && puoArchiviareEliminare && (
              <div className="flex gap-2 flex-shrink-0">
                {selectedCliente.status === 'archived' ? (
                  <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => handleUnarchive(selectedCliente.id)}>
                    Riattiva
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" icon={Archive} onClick={() => handleArchive(selectedCliente.id)}>
                    Archivia
                  </Button>
                )}
                <Button variant="secondary" size="sm" icon={Trash2} onClick={() => handleDelete(selectedCliente.id)}>
                  Elimina
                </Button>
              </div>
            )}
          </div>

          {/* Tabs — stesso stile della pagina interna del Copywriter */}
          {!isNuovo && (
            <div className="mb-4 sm:mb-6">
              <div className="border-b border-slate-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
                  <button
                    onClick={() => setSchedaTab('info')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                      schedaTab === 'info'
                        ? 'border-[#fdf751] text-slate-900 dark:text-slate-100'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <Building2 size={14} />
                    Informazioni
                  </button>
                  <button
                    onClick={() => setSchedaTab('report')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                      schedaTab === 'report'
                        ? 'border-[#fdf751] text-slate-900 dark:text-slate-100'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <FileText size={14} />
                    Report Social
                  </button>
                  <button
                    onClick={() => setSchedaTab('intel')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                      schedaTab === 'intel'
                        ? 'border-[#fdf751] text-slate-900 dark:text-slate-100'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <Radar size={14} />
                    News &amp; Competitor
                  </button>
                </nav>
              </div>
            </div>
          )}

          {/* Tab Info (o form Nuovo Cliente) */}
          {(isNuovo || schedaTab === 'info') && (
          <div className="max-w-5xl">
          <form onSubmit={handleSubmit}>
            {/* Layout 2-col su desktop: logo sx + tutti i campi dx — tutto in una sola schermata orizzontale */}
            <div className="lg:flex lg:gap-10 lg:items-start">
              {/* LEFT col (desktop): logo cliente */}
              <div className="lg:w-[200px] lg:flex-shrink-0 mb-4 lg:mb-0">
              {/* Logo Upload - Drag & Drop */}
              <div className="mb-2">
                <label className={ETICHETTA}>
                  Foto Profilo
                </label>
                <div
                  className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-full lg:mx-0 mx-auto overflow-hidden border transition-colors cursor-pointer ${
                    isViewMode
                      ? 'border-slate-200'
                      : 'border-slate-200 hover:border-slate-400'
                  }`}
                  onDragOver={(e) => {
                    if (isViewMode) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                  }}
                  onDrop={(e) => {
                    if (isViewMode) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setFormData({ ...formData, logoUrl: event.target?.result as string });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  onClick={() => {
                    if (isViewMode) return;
                    document.getElementById('logo-file-input')?.click();
                  }}
                >
                  {formData.logoUrl ? (
                    <>
                      <img
                        src={formData.logoUrl}
                        alt="Logo preview"
                        className="w-full h-full object-cover"
                      />
                      {!isViewMode && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs font-medium">Cambia</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-gray-700">
                      <span className="text-2xl font-medium mb-1">
                        {formData.nomeAzienda?.charAt(0)?.toUpperCase() || '+'}
                      </span>
                      {!isViewMode && (
                        <span className="text-[10px]">Trascina foto</span>
                      )}
                    </div>
                  )}
                </div>
                <input
                  id="logo-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={isViewMode}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setFormData({ ...formData, logoUrl: event.target?.result as string });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {formData.logoUrl && !isViewMode && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, logoUrl: '' })}
                    className="block lg:mx-0 mx-auto mt-2 text-xs text-slate-500 hover:text-slate-900"
                  >
                    Rimuovi foto
                  </button>
                )}
              </div>
              </div>
              {/* RIGHT col (desktop): tutti i campi + drive link */}
              <div className="lg:flex-1 lg:min-w-0">
                <div className="space-y-3 sm:space-y-4">
              {/* ── Anagrafica ── (lg: 3 col) */}
              <p className={TITOLO_SEZIONE}>Anagrafica</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="sm:col-span-2 lg:col-span-2">
                  <label className={ETICHETTA}>
                    Nome Azienda
                  </label>
                  <input
                    type="text"
                    value={formData.nomeAzienda}
                    onChange={(e) => setFormData({ ...formData, nomeAzienda: e.target.value })}
                    disabled={isViewMode}
                    className={CAMPO}
                    required
                  />
                </div>

                <div>
                  <label className={ETICHETTA}>
                    Referente Riprese
                  </label>
                  <input
                    type="text"
                    value={formData.referenteRiprese}
                    onChange={(e) => setFormData({ ...formData, referenteRiprese: e.target.value })}
                    disabled={isViewMode}
                    className={CAMPO}
                  />
                </div>

                <div>
                  <label className={ETICHETTA}>
                    Telefono
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    disabled={isViewMode}
                    className={CAMPO}
                    required
                  />
                </div>

                <div>
                  <label className={ETICHETTA}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={isViewMode}
                    className={CAMPO}
                  />
                </div>

                <div>
                  <label className={ETICHETTA}>
                    Reel Mensili
                  </label>
                  <input
                    type="number"
                    value={formData.reelMensili}
                    onChange={(e) => setFormData({ ...formData, reelMensili: parseInt(e.target.value) || 1 })}
                    disabled={isViewMode}
                    className={CAMPO}
                    min="1"
                    required
                  />
                </div>
              </div>

              {/* ── Lavorazione: chi scrive, quando si pubblica, quando si gira, dove ── */}
              <div className={`${SEZIONE} space-y-4`}>
                <p className={TITOLO_SEZIONE}>Lavorazione</p>
              {/* Chi scrive i testi: se li scrive il cliente, per noi non c'è
                  niente da scrivere e il lato testo risulta già coperto. */}
              <div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.testiDalCliente}
                    onChange={(e) => setFormData({ ...formData, testiDalCliente: e.target.checked })}
                    disabled={isViewMode}
                    /* Il colore della spunta arriva da `text-*` (plugin forms):
                       niente famiglie slate/gray, che il tema scuro ridipinge in
                       bianco con !important e renderebbero la "v" invisibile. */
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-gray-600 text-emerald-600 focus:ring-2 focus:ring-[#fdf751]/40 disabled:opacity-50"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                      I testi li scrive il cliente
                    </span>
                    <span className="block text-xs text-slate-400 dark:text-slate-500">
                      Spuntalo se i contenuti li scrive il cliente: non compariranno più tra i contenuti da scrivere
                    </span>
                  </span>
                </label>
              </div>

              {/* Post grafici: oltre ai reel, il calendario prevede post con
                  solo copy + immagine, alternati ai reel nel feed a scacchiera.
                  Niente riprese né montaggio: vanno in programmazione appena
                  il testo è confermato. */}
              <div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.postGrafici}
                    onChange={(e) => setFormData({ ...formData, postGrafici: e.target.checked })}
                    disabled={isViewMode}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-gray-600 text-emerald-600 focus:ring-2 focus:ring-[#fdf751]/40 disabled:opacity-50"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Il calendario prevede anche post grafici o caroselli foto
                    </span>
                    <span className="block text-xs text-slate-400 dark:text-slate-500">
                      Post senza riprese, alternati ai reel nel feed a scacchiera
                    </span>
                  </span>
                </label>
                {formData.postGrafici && (
                  <div className="mt-3 pl-6">
                    {/* Due cose diverse (03/11/2026, Michel): il post grafico è un carosello in computer
                        grafica, il carosello foto sono foto reali scattate nel locale. Insieme fanno i
                        giorni da post del calendario. */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={ETICHETTA}>
                          Post grafici a settimana
                        </label>
                        <input
                          type="number"
                          value={formData.postGraficiSettimana}
                          onChange={(e) => setFormData({ ...formData, postGraficiSettimana: Math.min(7 - formData.caroselliFotoSettimana, Math.max(0, parseInt(e.target.value) || 0)) })}
                          disabled={isViewMode}
                          className={CAMPO}
                          min="0"
                          max="7"
                        />
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">In computer grafica</p>
                      </div>
                      <div>
                        <label className={ETICHETTA}>
                          Caroselli foto a settimana
                        </label>
                        <input
                          type="number"
                          value={formData.caroselliFotoSettimana}
                          onChange={(e) => setFormData({ ...formData, caroselliFotoSettimana: Math.min(7 - formData.postGraficiSettimana, Math.max(0, parseInt(e.target.value) || 0)) })}
                          disabled={isViewMode}
                          className={CAMPO}
                          min="0"
                          max="7"
                        />
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Foto reali scattate nel locale</p>
                      </div>
                    </div>
                    {(() => {
                      // Anteprima del piano: reel e post a settimana, giorni di uscita e
                      // le prossime settimane, con gli stessi calcoli di Calendario e
                      // Pubblicazione (utils/pianoUscite.ts).
                      const finto = {
                        reelMensili: formData.reelMensili,
                        postGrafici: true,
                        postGraficiSettimana: formData.postGraficiSettimana + formData.caroselliFotoSettimana,
                        giorniPreferitiRiprese: formData.giorniPreferitiRiprese,
                      } as Cliente;
                      const anteprima = giorniUscita(finto);
                      const reelSett = Math.max(1, Math.round((formData.reelMensili || 0) / 4));
                      const postSett = formData.postGraficiSettimana + formData.caroselliFotoSettimana;
                      const regola = reelSett === postSett
                        ? 'alternati uno a uno (scacchiera)'
                        : reelSett > postSett
                        ? 'il post ruota di una casella a settimana, così non finisce mai nella stessa colonna del feed'
                        : 'il reel ruota di una casella a settimana';
                      return (
                        <div className="mt-1 text-xs text-slate-400 dark:text-slate-500 space-y-1">
                          <p>
                            Circa {reelSett} reel + {postSett} post a settimana ({formData.postGraficiSettimana} grafici, {formData.caroselliFotoSettimana} caroselli foto), {regola}.
                            Giorni di uscita: <span className="font-semibold text-slate-600 dark:text-slate-300">{anteprima.join(', ') || 'nessuno'}</span>
                            {anteprima.length > formData.giorniPreferitiRiprese.length ? ' (aggiunti in automatico ai giorni di pubblicazione scelti sotto)' : ''}
                          </p>
                          {anteprimaPiano(finto).map((riga) => (
                            <p key={riga} className="text-slate-500 dark:text-slate-400">{riga}</p>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Profili di pubblicazione: se il cliente pubblica su più
                  profili dallo stesso piano (142 Restaurant e 142 Bistrot),
                  ogni contenuto dice per quale è pensato, o per entrambi. */}
              <div>
                <label className={ETICHETTA}>
                  Profili di pubblicazione
                </label>
                <input
                  type="text"
                  value={formData.accountPubblicazione}
                  onChange={(e) => setFormData({ ...formData, accountPubblicazione: e.target.value })}
                  disabled={isViewMode}
                  className={CAMPO}
                  placeholder="Es. 142 Restaurant, 142 Bistrot"
                />
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  Solo se pubblica su più profili: separali con la virgola e su ogni contenuto potrai scegliere per quale è, o per tutti. Vuoto se il profilo è uno solo
                </p>
              </div>

              <div>
                <label className={ETICHETTA}>
                  Orario di Pubblicazione
                </label>
                <input
                  type="time"
                  value={formData.orarioPubblicazione}
                  onChange={(e) => setFormData({ ...formData, orarioPubblicazione: e.target.value })}
                  disabled={isViewMode}
                  className={CAMPO}
                />
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Orario preferito di pubblicazione dei contenuti (default 21:00)</p>
              </div>

              <div>
                {/* Sono i giorni in cui il cliente esce: li leggono Calendario
                    e Pubblicazione. Il nome del campo (giorniPreferitiRiprese)
                    è storico. */}
                <label className={ETICHETTA}>
                  Giorni di Pubblicazione
                </label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {giorniSettimana.map((giorno) => (
                    <button
                      key={giorno}
                      type="button"
                      onClick={() => toggleGiorno(giorno)}
                      disabled={isViewMode}
                      className={CHIP(formData.giorniPreferitiRiprese.includes(giorno))}
                    >
                      {giorno.substring(0, 3)}
                      {/* colore sullo span: index.css tinge gli span nudi */}
                      <span className={`hidden sm:inline ${formData.giorniPreferitiRiprese.includes(giorno) ? 'text-[#ffffff]' : 'text-slate-600'}`}>{giorno.substring(3)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={ETICHETTA}>
                  Piattaforme
                </label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {Object.values(PiattaformaSocial).map((piattaforma) => (
                    <button
                      key={piattaforma}
                      type="button"
                      onClick={() => togglePiattaforma(piattaforma)}
                      disabled={isViewMode}
                      className={CHIP(formData.piattaforme.includes(piattaforma))}
                    >
                      {piattaforma}
                    </button>
                  ))}
                </div>
              </div>
              </div>{/* fine Lavorazione */}

              {/* ── Accessi social: per ogni piattaforma selezionata, nome pagina + password ── */}
              {CAMPI_SOCIAL.some((social) => formData.piattaforme.includes(social.piattaforma)) && (
              <div className={`${SEZIONE} space-y-4`}>
                <p className={TITOLO_SEZIONE}>Accessi social</p>
                {CAMPI_SOCIAL.filter((social) => formData.piattaforme.includes(social.piattaforma)).map((social) => (
                  <div
                    key={social.piattaforma}
                    className="grid grid-cols-1 sm:grid-cols-[140px_1fr_1fr] gap-3 sm:gap-4 sm:items-start"
                  >
                    <p className="text-sm font-medium text-slate-900 sm:pt-6">
                      {social.piattaforma}
                    </p>
                    <div className="contents">
                      <div>
                        <label className={ETICHETTA}>
                          {social.etichettaNome}
                        </label>
                        <input
                          type="text"
                          value={formData[social.campoNome]}
                          onChange={(e) => setFormData((prev) => ({ ...prev, [social.campoNome]: e.target.value }))}
                          disabled={isViewMode}
                          placeholder={social.placeholderNome}
                          className={CAMPO}
                        />
                        {social.notaNome && (
                          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{social.notaNome}</p>
                        )}
                      </div>

                      <div>
                        <label className={ETICHETTA}>
                          Password {social.piattaforma}
                        </label>
                        <input
                          type="text"
                          value={formData[social.campoPassword]}
                          onChange={(e) => setFormData((prev) => ({ ...prev, [social.campoPassword]: e.target.value }))}
                          disabled={isViewMode}
                          className={CAMPO}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>

            {/* ── Collegamenti: Google Drive + Web App Lead ── */}
            <div className={`${SEZIONE} mt-4`}>
            <p className={TITOLO_SEZIONE}>Collegamenti</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              {/* Google Drive Link */}
              <div>
                <label className={ETICHETTA}>
                  Link Google Drive
                </label>
                <input
                  type="url"
                  value={formData.linkGoogleDrive}
                  onChange={(e) => setFormData({ ...formData, linkGoogleDrive: e.target.value })}
                  disabled={isViewMode}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className={CAMPO}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Link alla cartella Google Drive condivisa per i contenuti del cliente
                </p>
                {formData.linkGoogleDrive && !isViewMode && (
                  <a
                    href={formData.linkGoogleDrive}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 mt-1 transition-colors"
                  >
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Visualizza cartella
                  </a>
                )}
              </div>

              {/* Web App Lead Link */}
              <div>
                <label className={ETICHETTA}>
                  Link Web App Lead
                </label>
                <input
                  type="url"
                  value={formData.linkWebAppLead}
                  onChange={(e) => setFormData({ ...formData, linkWebAppLead: e.target.value })}
                  disabled={isViewMode}
                  placeholder="https://..."
                  className={CAMPO}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Link al form Web App Lead dedicato a questo cliente
                </p>
                {formData.linkWebAppLead && !isViewMode && (
                  <a
                    href={formData.linkWebAppLead}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 mt-1 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Apri form lead
                  </a>
                )}
              </div>
            </div>
            </div>{/* fine Collegamenti */}
              </div>{/* fine right col */}
            </div>{/* fine lg:flex wrapper */}

            {/* Form buttons - stacked on mobile */}
            <div className="mt-5 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <Button
                type="button"
                onClick={chiudiScheda}
                variant="secondary"
                className="w-full sm:w-auto justify-center"
              >
                Annulla
              </Button>
              {!isViewMode && (
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full sm:w-auto justify-center"
                >
                  {selectedCliente ? 'Salva Modifiche' : 'Crea Cliente'}
                </Button>
              )}
            </div>
          </form>
          </div>
          )}

          {/* Tab Report Social: generatore (template bloccato) + storico report */}
          {!isNuovo && schedaTab === 'report' && selectedCliente && (
            <div className="space-y-4 sm:space-y-6">
              {igNonCollegato && (
                <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30 px-4 py-3 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-700 dark:text-red-300">Instagram non collegato</p>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      Questo cliente non ha un collegamento diretto con l'account Instagram nel Business Manager:
                      la generazione dei report non funzionerà finché il profilo IG non viene collegato.
                    </p>
                  </div>
                </div>
              )}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
                <ReportGeneratorModal
                  inline
                  isOpen
                  onClose={chiudiScheda}
                  clienteId={selectedCliente.id}
                  clienteNome={selectedCliente.nomeAzienda}
                  onGenerated={() => setReportRefreshKey((k) => k + 1)}
                />
              </div>
              <ReportStorico clienteId={selectedCliente.id} refreshKey={reportRefreshKey} />
            </div>
          )}

          {/* Tab News & Competitor: anagrafica delle fonti (competitor, testate,
              argomenti) che alimentano la sezione vista dal cliente */}
          {!isNuovo && schedaTab === 'intel' && selectedCliente && (
            <ClientIntelSources
              clienteId={selectedCliente.id}
              clienteNome={selectedCliente.nomeAzienda}
              readOnly={isViewMode}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Clienti;
